/* ──────────────────────────────────────────────────────────────────────
 *  ContactPicker — Debounced type-ahead for selecting L4E contacts.
 *
 *  Searches the current user's accepted contacts server-side via
 *  GET /api/contacts/search?q=<query>. Results show avatar, name,
 *  screen name, and an L4E badge. Selecting a result fires onSelect
 *  with the full ContactSearchResult.
 *
 *  Used by TeamMemberEditor's "Link L4E Account" flow and anywhere
 *  else a contact picker is needed.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { searchContacts } from '../../api/chat';
import type { ContactSearchResult } from '../../api/chat';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './ContactPicker.module.css';

/* ── Props ────────────────────────────────────────────────────────── */

export interface ContactPickerProps {
  /** Called when a contact is selected from the dropdown. */
  onSelect: (contact: ContactSearchResult) => void;
  /** Called when the picker is dismissed without a selection. */
  onCancel?: () => void;
  /** GUIDs to exclude from results (already-linked users, self, etc.) */
  excludeGuids?: string[];
  /** Placeholder text override */
  placeholder?: string;
  /** Auto-focus the input on mount. Default true. */
  autoFocus?: boolean;
}

/* ── Constants ────────────────────────────────────────────────────── */

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/* ── Component ────────────────────────────────────────────────────── */

export default function ContactPicker({
  onSelect,
  onCancel,
  excludeGuids = [],
  placeholder,
  autoFocus = true,
}: ContactPickerProps) {
  const { t } = useAppTranslation('common');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();

  // Exclude set (case-insensitive)
  const excludeSet = useRef(new Set(excludeGuids.map(g => g.toUpperCase())));
  useEffect(() => {
    excludeSet.current = new Set(excludeGuids.map(g => g.toUpperCase()));
  }, [excludeGuids]);

  // Auto-focus
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        onCancel?.();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onCancel]);

  // Debounced search
  const doSearch = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = q.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsOpen(false);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await searchContacts(trimmed);
        // Filter out excluded GUIDs client-side
        const filtered = data.filter(
          r => !excludeSet.current.has(r.userGuid.toUpperCase())
        );
        setResults(filtered);
        setIsOpen(true);
        setHasSearched(true);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleSelect = useCallback((contact: ContactSearchResult) => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    onSelect(contact);
  }, [onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      onCancel?.();
    }
  }, [onCancel]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.inputRow}>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder={placeholder || t('contactPicker.placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            doSearch(e.target.value);
          }}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {isLoading && <span className={styles.spinner} />}
        {onCancel && (
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
          >
            {t('contactPicker.cancel')}
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className={styles.dropdown}>
          {results.map((contact) => (
            <button
              key={contact.userGuid}
              type="button"
              className={styles.resultRow}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(contact);
              }}
            >
              <div className={styles.avatar}>
                {contact.profileImageUrl ? (
                  <img
                    src={mediaSrc(contact.profileImageUrl) ?? undefined}
                    alt=""
                    className={styles.avatarImg}
                  />
                ) : (
                  <span className={styles.avatarInitial}>
                    {(contact.displayName || '?')[0].toUpperCase()}
                  </span>
                )}
                <span className={styles.l4eBadge} title={t('contactPicker.l4eMember')} />
              </div>
              <div className={styles.info}>
                <span className={styles.name}>{contact.displayName}</span>
                {contact.screenName && (
                  <span className={styles.handle}>@{contact.screenName}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && hasSearched && !isLoading && results.length === 0 && (
        <div className={styles.dropdown}>
          <div className={styles.noResults}>
            {t('contactPicker.noResults')}
          </div>
        </div>
      )}
    </div>
  );
}
